import * as React from 'react';
import * as styles from './MonthSurface.scss';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type CalendarMonthConfig from '../interfaces/CalendarMonthConfig';
import FocusableSurfaceComponent from './FocusableSurfaceComponent';
import MonthItemsPanel from './MonthItemsPanel';
import type SurfaceStore from '../interfaces/SurfaceStore';
import { StoreContextProvider } from './StoreContextProvider';

export default observer(function MonthSurface({
    config: { contentGenerator, setFocusedElement, surfaceComponent, monthSurfaceComponent },
    store,
}: {
    config: CalendarMonthConfig;
    store: SurfaceStore;
}) {
    const { content, columnHeaders, tabOrder, rowHeaders } = contentGenerator();
    const showRowHeaders = !!rowHeaders;

    const columnHeadersClassName = classNames(styles.header, {
        [styles.shiftedHeader]: showRowHeaders,
    });

    const itemsPanelClassName = classNames(styles.itemsPanel, {
        [styles.shiftedItemsPanel]: showRowHeaders,
    });

    const tabOrderProvider = React.useCallback(() => tabOrder, [tabOrder]);

    const { component, additionalProps } = monthSurfaceComponent || {};
    const MonthSurfaceComponentType = component || MonthItemsPanel;

    // aria-hidden column and row headers because events themselves say what days they are on, so don't need to read table headings
    return (
        <FocusableSurfaceComponent
            setFocusedElement={setFocusedElement}
            tabOrderProvider={tabOrderProvider}
            ref={surfaceComponent}>
            <StoreContextProvider value={store}>
                <div className={styles.page}>
                    <div className={columnHeadersClassName} aria-hidden="true">
                        {columnHeaders}
                    </div>
                    {showRowHeaders && (
                        <div className={styles.rowHeaders} aria-hidden="true">
                            {rowHeaders}
                        </div>
                    )}
                    <MonthSurfaceComponentType
                        key={'monthItemsPanel'}
                        className={itemsPanelClassName}
                        additionalProps={additionalProps}>
                        {content}
                    </MonthSurfaceComponentType>
                </div>
            </StoreContextProvider>
        </FocusableSurfaceComponent>
    );
});
